import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Step,
  StepButton,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ReactNode, useState } from 'react';

import type { MarkingMetricsSettings, MarkingReadiness } from '../../types';

interface MarkingMetricsGuideDialogProps {
  open: boolean;
  onClose: () => void;
  settings: MarkingMetricsSettings | null;
  readinessStates: MarkingReadiness[];
  readinessSubtitles: Record<MarkingReadiness, string>;
}

const pct = (value: number): string => `${Math.round(value * 100)}%`;

// A single worked example that runs through the precision, recall, and F1 pages
// so the reader can follow one set of numbers rather than several.
const EXAMPLE = {
  criterion: 'Uses a fronted adverbial',
  sentences: 20,
  trulyPositive: 8,
  aiMarkedPositive: 10,
  truePositives: 6,
};
const EXAMPLE_FALSE_POSITIVES = EXAMPLE.aiMarkedPositive - EXAMPLE.truePositives; // 4
const EXAMPLE_FALSE_NEGATIVES = EXAMPLE.trulyPositive - EXAMPLE.truePositives; // 2
const EXAMPLE_PRECISION = EXAMPLE.truePositives / EXAMPLE.aiMarkedPositive; // 0.6
const EXAMPLE_RECALL = EXAMPLE.truePositives / EXAMPLE.trulyPositive; // 0.75
const EXAMPLE_F1 = (2 * EXAMPLE_PRECISION * EXAMPLE_RECALL) / (EXAMPLE_PRECISION + EXAMPLE_RECALL); // 0.667

const EXAMPLE_CLASS_F1: Array<{ klass: string; f1: number; share: string }> = [
  { klass: 'positive', f1: EXAMPLE_F1, share: '40%' },
  { klass: 'negative', f1: 0.9, share: '50%' },
  { klass: 'both', f1: 0.4, share: '3%' },
  { klass: 'no attempt', f1: 0.95, share: '7%' },
];
const EXAMPLE_MACRO_F1 =
  EXAMPLE_CLASS_F1.reduce((total, entry) => total + entry.f1, 0) / EXAMPLE_CLASS_F1.length;

function Formula({ children }: { children: ReactNode }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        my: 1.5,
        bgcolor: 'grey.50',
        fontFamily: 'monospace',
        fontSize: '0.95rem',
        whiteSpace: 'pre-wrap',
      }}
    >
      {children}
    </Paper>
  );
}

function ExampleTable({
  columns,
  rows,
  caption,
}: {
  columns: string[];
  rows: Array<Array<ReactNode>>;
  caption?: string;
}) {
  return (
    <Paper variant="outlined" sx={{ my: 1.5, overflow: 'hidden' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map(column => (
              <TableCell key={column} sx={{ fontWeight: 'bold' }}>
                {column}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, rowIndex) => (
            <TableRow key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <TableCell key={cellIndex}>{cell}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {caption && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', p: 1 }}>
          {caption}
        </Typography>
      )}
    </Paper>
  );
}

function Term({ children }: { children: ReactNode }) {
  return (
    <Box component="strong" sx={{ fontWeight: 'bold' }}>
      {children}
    </Box>
  );
}

interface GuidePage {
  label: string;
  title: string;
  render: (props: MarkingMetricsGuideDialogProps) => ReactNode;
}

const PAGES: GuidePage[] = [
  {
    label: 'The question',
    title: 'What this page answers',
    render: () => (
      <>
        <Typography paragraph>
          For every criterion the AI marks, this page answers one question:{' '}
          <Term>can we trust the AI&apos;s marks for this criterion?</Term>
        </Typography>
        <Typography paragraph>
          To answer it, we compare two sets of marks on the same pieces of student writing:
        </Typography>
        <ExampleTable
          columns={['', 'Who marks it', 'What we call it']}
          rows={[
            [
              'Human',
              'Labellers, plus any disagreement reviews that resolved conflicts',
              'Ground truth',
            ],
            ['AI', 'The latest evaluation run of the current policy version', 'AI marks'],
          ]}
        />
        <Typography paragraph>
          Where the two agree, the AI is behaving like a human marker. Where they disagree, the AI
          is either awarding credit a human would not (over-marking) or missing credit a human would
          give (under-marking). The metrics on this page measure how often each of those happens,
          and the <Term>readiness</Term> column turns that into a single verdict.
        </Typography>
        <Alert severity="info">
          Throughout this guide we follow one example criterion:{' '}
          <Term>&ldquo;{EXAMPLE.criterion}&rdquo;</Term>, marked at sentence level.
        </Alert>
      </>
    ),
  },
  {
    label: 'Evidence',
    title: 'Ground truth, coverage, and evidence',
    render: ({ settings }) => {
      const sentenceThresholds = settings?.evidenceThresholds.sentence;
      return (
        <>
          <Typography paragraph>
            Before we can measure anything, we need enough <Term>ground-truth cases</Term>: pieces
            of writing where a human has decided whether the criterion is met. Those come from
            explicit labels and from resolved disagreement reviews.
          </Typography>
          <Typography paragraph>
            We can only compare a case if the AI has also marked it{' '}
            <Term>with the current policy version</Term>. The <Term>Evaluation coverage</Term>{' '}
            column shows both numbers as <Term>GT / AI marked</Term>:
          </Typography>
          <ExampleTable
            columns={['Shown as', 'Meaning']}
            rows={[
              [
                <Term key="v">12 / 9</Term>,
                '12 ground-truth cases exist; 9 of them have a current AI mark, so 9 are compared. The other 3 need an eval run before they count.',
              ],
              [
                <Term key="g">Needs 8 cases · 2 transcriptions</Term>,
                'How far the compared set is from the evidence threshold for this granularity.',
              ],
            ]}
          />
          <Typography component="div" sx={{ mb: 2 }}>
            The <Term>Evidence</Term> column is the gatekeeper. A criterion is{' '}
            <Chip size="small" color="success" label="sufficient" sx={{ mx: 0.5 }} /> only when the
            compared cases meet the minimum count <em>and</em> come from enough distinct
            transcriptions (so a single unusual essay cannot dominate the result).
            {sentenceThresholds?.minComparedCases != null &&
              sentenceThresholds.minDistinctTranscriptions != null && (
                <>
                  {' '}
                  Sentence-level criteria currently need at least{' '}
                  <Term>{sentenceThresholds.minComparedCases} compared cases</Term> from{' '}
                  <Term>{sentenceThresholds.minDistinctTranscriptions} transcriptions</Term>.
                </>
              )}
          </Typography>
          <Alert severity="warning">
            Until evidence is sufficient, the performance numbers are shown but greyed out and the
            readiness is <Term>Insufficient evidence</Term>. A 100% score on 3 cases tells us very
            little.
          </Alert>
        </>
      );
    },
  },
  {
    label: 'Exact agreement',
    title: 'Four classes and exact agreement',
    render: () => (
      <>
        <Typography paragraph>
          Every mark, human or AI, is one of four <Term>classes</Term>:
        </Typography>
        <ExampleTable
          columns={['Class', 'Meaning for “' + EXAMPLE.criterion + '”']}
          rows={[
            [
              <Chip key="p" size="small" label="positive" />,
              'The sentence uses a fronted adverbial correctly.',
            ],
            [
              <Chip key="n" size="small" label="negative" />,
              'The sentence does not use one, or uses one incorrectly.',
            ],
            [
              <Chip key="b" size="small" label="both" />,
              'The sentence contains both a correct and an incorrect use.',
            ],
            [
              <Chip key="na" size="small" label="no attempt" />,
              'The student did not attempt a fronted adverbial at all.',
            ],
          ]}
        />
        <Typography paragraph>
          <Term>Exact agreement</Term> is the simplest metric: the share of compared cases where the
          AI chose the <em>same</em> class as the human.
        </Typography>
        <Formula>Exact agreement = cases where AI class = human class ÷ compared cases</Formula>
        <Typography paragraph>
          If 20 sentences are compared and the AI matches the human on 17 of them, exact agreement
          is <Term>17 ÷ 20 = 85%</Term>.
        </Typography>
        <Alert severity="info">
          Exact agreement is easy to read but it hides <em>which</em> class the AI gets wrong. If
          almost every sentence is “negative”, an AI that always says “negative” scores well here
          while being useless at spotting the positives. The next pages fix that.
        </Alert>
      </>
    ),
  },
  {
    label: 'Precision',
    title: 'Precision: when the AI says “yes”, is it right?',
    render: () => (
      <>
        <Typography component="div" sx={{ mb: 2 }}>
          Precision and recall look at <Term>one class at a time</Term>. Here we take the{' '}
          <Chip size="small" label="positive" sx={{ mx: 0.5 }} /> class for our example.
        </Typography>
        <Typography paragraph>
          Out of {EXAMPLE.sentences} compared sentences, humans marked{' '}
          <Term>{EXAMPLE.trulyPositive}</Term> as positive. The AI marked{' '}
          <Term>{EXAMPLE.aiMarkedPositive}</Term> as positive. Only{' '}
          <Term>{EXAMPLE.truePositives}</Term> of the AI&apos;s positives were sentences the humans
          also called positive.
        </Typography>
        <ExampleTable
          columns={['', 'Human said positive', 'Human said something else']}
          rows={[
            [
              <Term key="a">AI said positive</Term>,
              `${EXAMPLE.truePositives} correct (true positives)`,
              `${EXAMPLE_FALSE_POSITIVES} wrong (false positives)`,
            ],
            [
              <Term key="b">AI said something else</Term>,
              `${EXAMPLE_FALSE_NEGATIVES} missed (false negatives)`,
              `${EXAMPLE.sentences - EXAMPLE.trulyPositive - EXAMPLE_FALSE_POSITIVES} correct (true negatives)`,
            ],
          ]}
          caption="This is the “one-vs-rest confusion” shown for each class in the criterion drawer."
        />
        <Typography paragraph>
          <Term>Precision</Term> asks: of everything the AI marked positive, how much really was?
        </Typography>
        <Formula>
          Precision = true positives ÷ everything the AI marked positive{'\n'}
          {'          '}= {EXAMPLE.truePositives} ÷ {EXAMPLE.aiMarkedPositive} ={' '}
          {pct(EXAMPLE_PRECISION)}
        </Formula>
        <Alert severity="info">
          <Term>Low precision means over-marking.</Term> {EXAMPLE_FALSE_POSITIVES} students would be
          told they used a fronted adverbial when a teacher would say they had not.
        </Alert>
      </>
    ),
  },
  {
    label: 'Recall',
    title: 'Recall: of the real “yes” cases, how many did the AI find?',
    render: () => (
      <>
        <Typography paragraph>
          Same sentences, same class, opposite question. Humans marked{' '}
          <Term>{EXAMPLE.trulyPositive}</Term> sentences as positive. The AI found{' '}
          <Term>{EXAMPLE.truePositives}</Term> of them and missed{' '}
          <Term>{EXAMPLE_FALSE_NEGATIVES}</Term>.
        </Typography>
        <Typography paragraph>
          <Term>Recall</Term> asks: of everything that really was positive, how much did the AI
          catch?
        </Typography>
        <Formula>
          Recall = true positives ÷ everything the human marked positive{'\n'}
          {'       '}= {EXAMPLE.truePositives} ÷ {EXAMPLE.trulyPositive} = {pct(EXAMPLE_RECALL)}
        </Formula>
        <ExampleTable
          columns={['Metric', 'Denominator', 'Our example', 'When it is low']}
          rows={[
            [
              <Term key="p">Precision</Term>,
              'What the AI said was positive',
              pct(EXAMPLE_PRECISION),
              'AI over-marks: credit given that a teacher would not give',
            ],
            [
              <Term key="r">Recall</Term>,
              'What was really positive',
              pct(EXAMPLE_RECALL),
              'AI under-marks: credit withheld that a teacher would give',
            ],
          ]}
        />
        <Alert severity="info">
          The two pull against each other. An AI that marks <em>every</em> sentence positive has
          100% recall (it never misses one) but terrible precision. An AI that only marks the most
          obvious sentence positive has high precision but poor recall. We need both.
        </Alert>
      </>
    ),
  },
  {
    label: 'F1 and Macro F1',
    title: 'F1 and Macro F1: one number for all four classes',
    render: ({ settings }) => (
      <>
        <Typography paragraph>
          <Term>F1</Term> combines precision and recall for one class into a single score. It is the{' '}
          <em>harmonic</em> mean, which punishes imbalance: a class can only score well on F1 if
          both precision and recall are good.
        </Typography>
        <Formula>
          F1 = 2 × precision × recall ÷ (precision + recall){'\n'}
          {'   '}= 2 × {EXAMPLE_PRECISION.toFixed(2)} × {EXAMPLE_RECALL.toFixed(2)} ÷ (
          {EXAMPLE_PRECISION.toFixed(2)} + {EXAMPLE_RECALL.toFixed(2)}) = {pct(EXAMPLE_F1)}
        </Formula>
        <Typography paragraph>
          Compare that with precision 100% and recall 10%: the plain average would be 55%, but F1 is
          only <Term>18%</Term>, which better reflects an AI that misses 9 in 10 positives.
        </Typography>
        <Typography paragraph>
          <Term>Macro F1</Term> then takes the F1 for each of the four classes and averages them
          with <em>equal weight</em>, regardless of how common each class is:
        </Typography>
        <ExampleTable
          columns={['Class', 'Share of cases', 'F1']}
          rows={[
            ...EXAMPLE_CLASS_F1.map(entry => [
              <Chip key={entry.klass} size="small" label={entry.klass} />,
              entry.share,
              pct(entry.f1),
            ]),
            [
              <Term key="m">Macro F1</Term>,
              '',
              <Term key="mv">
                ({EXAMPLE_CLASS_F1.map(entry => pct(entry.f1)).join(' + ')}) ÷ 4 ={' '}
                {pct(EXAMPLE_MACRO_F1)}
              </Term>,
            ],
          ]}
        />
        <Alert severity="info">
          <Term>Why macro?</Term> The “both” class is only 3% of cases, so an AI that handles it
          badly barely dents exact agreement. In Macro F1 it counts for a full quarter of the score,
          so a weak class cannot hide behind the common ones. Exact agreement and Macro F1 often
          disagree for exactly this reason.
        </Alert>
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          <Term>Too few cases to count.</Term> Equal weighting cuts both ways: a class with only one
          or two ground-truth cases would swing the whole average on a single decision. So a class
          only joins the macro average once it has at least{' '}
          <Term>
            {settings?.minClassSupport ?? 5} ground-truth case
            {(settings?.minClassSupport ?? 5) === 1 ? '' : 's'}
          </Term>
          . Smaller classes are still shown per class in the details drawer, marked as not counted.
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          Macro precision and macro recall are the same idea applied to precision and recall, and
          are shown alongside Macro F1 so you can tell whether a low score is over- or
          under-marking.
        </Typography>
      </>
    ),
  },
  {
    label: 'Readiness',
    title: 'Readiness: turning the numbers into a verdict',
    render: ({ settings, readinessStates, readinessSubtitles }) => (
      <>
        <Typography paragraph>
          Readiness combines three checks into one label per criterion:
        </Typography>
        <ExampleTable
          columns={['Check', 'Passes when']}
          rows={[
            [
              <Term key="e">Evidence</Term>,
              'Compared cases and distinct transcriptions meet the thresholds for the granularity.',
            ],
            [
              <Term key="p">Performance</Term>,
              settings ? (
                <>
                  Exact agreement ≥ <Term>{pct(settings.exactAgreementThreshold)}</Term> and Macro
                  F1 ≥ <Term>{pct(settings.macroF1Threshold)}</Term> (the saved thresholds).
                </>
              ) : (
                'Exact agreement and Macro F1 both reach the saved thresholds.'
              ),
            ],
            [
              <Term key="l">Live</Term>,
              'The criterion is currently active and markable in production.',
            ],
          ]}
        />
        <Typography paragraph>Those checks produce one of five states:</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {readinessStates.map(state => (
            <Box key={state} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Chip size="small" label={state} sx={{ minWidth: 170 }} />
              <Typography variant="body2" color="text.secondary">
                {readinessSubtitles[state]}
              </Typography>
            </Box>
          ))}
        </Box>
        <Alert severity="info" sx={{ mt: 2 }}>
          The ones to act on first are <Term>Needs attention</Term> (live but under-performing) and{' '}
          <Term>Insufficient evidence</Term> on live criteria (in production without proof). Use the
          group-by controls to bring them to the top.
        </Alert>
      </>
    ),
  },
];

export default function MarkingMetricsGuideDialog(props: MarkingMetricsGuideDialogProps) {
  const { open, onClose } = props;
  const [step, setStep] = useState(0);
  const page = PAGES[step];
  const isLast = step === PAGES.length - 1;

  const handleClose = () => {
    onClose();
    setStep(0);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="marking-metrics-guide-title"
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            id="marking-metrics-guide-title"
            variant="h6"
            component="span"
            sx={{ flexGrow: 1 }}
          >
            How marking metrics work
          </Typography>
          <IconButton aria-label="Close guide" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Stepper
          nonLinear
          activeStep={step}
          alternativeLabel
          sx={{ mb: 3, display: { xs: 'none', sm: 'flex' } }}
        >
          {PAGES.map((entry, index) => (
            <Step key={entry.label} completed={false}>
              <StepButton onClick={() => setStep(index)}>{entry.label}</StepButton>
            </Step>
          ))}
        </Stepper>
        <Typography variant="h6" component="h2" sx={{ mb: 1.5 }}>
          {page.title}
        </Typography>
        {page.render(props)}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
        <Typography variant="body2" color="text.secondary">
          {step + 1} of {PAGES.length}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button disabled={step === 0} onClick={() => setStep(current => current - 1)}>
            Back
          </Button>
          {isLast ? (
            <Button variant="contained" onClick={handleClose}>
              Done
            </Button>
          ) : (
            <Button variant="contained" onClick={() => setStep(current => current + 1)}>
              Next
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
}
